import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useRouteError } from "react-router-dom";
import {
  Home,
  RefreshCcw,
  ArrowLeft,
  Mail,
  Copy,
  Check,
  Download,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFormatDate } from "../utils/date/useFormatDate";

/* ================= UUID ================= */
/** Where the "Contact Support" button on the crash screen routes to. */
const SUPPORT_EMAIL = "contactus@emeistechnologies.com";

const generateErrorId = () =>
  "ERR-" + Math.random().toString(36).substring(2, 10).toUpperCase();

interface ProbedError {
  message?: string;
  stack?: string;
  response?: { data?: unknown };
}

function asProbedError(error: unknown): ProbedError {
  return typeof error === "object" && error !== null
    ? (error as ProbedError)
    : {};
}

export default function ErrorFallback() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const formatDate = useFormatDate();
  const routeError = useRouteError();
  const error = asProbedError(routeError);

  const [scrolled, setScrolled] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorId] = useState(generateErrorId());

  const [showDetails, setShowDetails] = useState(false);
  const [openSections, setOpenSections] = useState({
    stack: true,
    raw: false,
  });

  const stackRef = useRef<HTMLDivElement>(null);
  const rawRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ================= ERROR TYPE ================= */
  const errorType = useMemo(() => {
    const msg = error.message?.toLowerCase() || "";

    if (msg.includes("network")) return "Network Error";
    if (msg.includes("latlng")) return "Map Error";
    if (msg.includes("undefined")) return "Data Error";

    return "Unknown Error";
  }, [error]);

  /* ================= DETAILS ================= */
  const errorDetails = useMemo(() => {
    const path = window.location.pathname.replace("/", "");

    const stack =
      error.stack ||
      error.message ||
      (error.response?.data !== undefined
        ? String(error.response.data)
        : undefined) ||
      (typeof routeError === "string"
        ? routeError
        : JSON.stringify(routeError, null, 2));

    return {
      id: errorId,
      message: error.message || "Unknown error",
      stack,
      raw: JSON.stringify(routeError, null, 2),
      path,
      time: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
  }, [error, routeError, errorId]);

  /* ================= COPY ================= */
  const fullLog = `
Error ID: ${errorDetails.id}
Type: ${errorType}
Path: ${errorDetails.path}
Time: ${errorDetails.time}

Message:
${errorDetails.message}

Stack:
${errorDetails.stack || "No stack available"}

Raw:
${errorDetails.raw}

Browser:
${errorDetails.userAgent}
`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullLog);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ================= DOWNLOAD ================= */
  const handleDownload = () => {
    const blob = new Blob([fullLog], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${errorDetails.id}.log`;
    a.click();

    URL.revokeObjectURL(url);
  };

  /* ================= MAIL ================= */
  const handleMail = () => {
    // Named rather than inline so the address lives in one place if support
    // routing changes again.
    const subject = encodeURIComponent(
      `[${errorType}] (${errorDetails.id}) ${errorDetails.path}`,
    );

    const body = encodeURIComponent(fullLog);

    window.open(
      `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`,
      "_blank",
    );
  };

  const toggle = (key: "stack" | "raw") =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] text-white flex flex-col overflow-y-auto">
      {/* ================= HEADER ================= */}
      <header
        className={`
                    w-full border-b sticky top-0 z-50
                    bg-white/80 dark:bg-slate-900/80
                    backdrop-blur-md
                    border-slate-200 dark:border-slate-800
                    ${scrolled ? "shadow-md" : ""}
                `}
      >
        <div
          className="
                        max-w-7xl mx-auto
                        px-4 sm:px-6 py-3 sm:py-4
                        flex items-center justify-between
                    "
        >
          <div className="flex items-center gap-4">
            <img
              src="/pactline.png"
              alt="PACTLINE"
              className="h-10 sm:h-11 md:h-12 w-auto shrink-0 object-contain"
            />

            <div>
              <h1
                className="
                                    text-lg font-semibold
                                    text-slate-800 dark:text-white
                                "
              >
                Pactline
              </h1>

              <p
                className="
                                    text-xs
                                    text-slate-500 dark:text-slate-400
                                "
              >
                Contracts Studio | Obligations Hub | Receivables Engine
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* ================= CONTENT ================= */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-3xl text-center space-y-8">
          {/* TITLE */}
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              {t("errorPage.title")}
            </h1>

            <p className="text-gray-400 text-sm">
              {t("errorPage.whileLoading")}{" "}
              <span className="text-blue-400 font-medium">
                {errorDetails.path || "dashboard"}
              </span>
            </p>
          </div>

          {/* BADGES */}
          <div className="flex justify-center gap-3 flex-wrap">
            <span className="px-3 py-1 rounded-full text-xs bg-red-500/10 text-red-600 border border-red-500/20">
              {errorType}
            </span>

            <span className="px-3 py-1 rounded-full text-xs bg-white/5 text-gray-300 border border-white/10">
              {errorDetails.id}
            </span>
          </div>

          {/* ERROR CARD */}
          <div
            className="
                        bg-white/5 backdrop-blur-xl
                        border border-white/10
                        rounded-2xl p-6 text-left space-y-4
                        shadow-[0_0_40px_rgba(0,0,0,0.3)]
                    "
          >
            <div className="grid gap-2 text-sm">
              <p>
                <span className="text-gray-400">{t("errorPage.message")}</span>{" "}
                {errorDetails.message}
              </p>
              <p>
                <span className="text-gray-400">{t("errorPage.time")}</span>{" "}
                {formatDate(errorDetails.time, { showTime: true })}
              </p>
              <p>
                <span className="text-gray-400">{t("errorPage.path")}</span>{" "}
                {errorDetails.path || "dashboard"}
              </p>
            </div>

            {/* DEBUG BUTTON */}
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="
                                mt-3 px-4 py-2 rounded-lg text-sm
                                bg-blue-600/20 hover:bg-blue-600/30
                                text-blue-400 border border-blue-500/20
                                transition flex items-center gap-2
                            "
            >
              {showDetails ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
              {showDetails ? t("errorPage.hideDebug") : t("errorPage.showDebug")}
            </button>

            {/* DEBUG PANEL */}
            {showDetails && (
              <div className="mt-4 space-y-3">
                {/* Stack Section */}
                <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
                  <button
                    onClick={() => toggle("stack")}
                    className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-medium text-gray-300 bg-white/5 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2">
                      {openSections.stack ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      {t("errorPage.stackTrace")}
                    </span>
                    <span className="text-[11px] text-gray-500 font-mono">
                      error.stack
                    </span>
                  </button>
                  {openSections.stack && (
                    <div
                      ref={stackRef}
                      className="p-4 text-xs text-gray-300 font-mono whitespace-pre-wrap max-h-48 overflow-auto border-t border-white/5"
                    >
                      {errorDetails.stack || "No stack available"}
                    </div>
                  )}
                </div>

                {/* Raw Error Section */}
                <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
                  <button
                    onClick={() => toggle("raw")}
                    className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-medium text-gray-300 bg-white/5 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2">
                      {openSections.raw ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      {t("errorPage.rawError")}
                    </span>
                    <span className="text-[11px] text-gray-500 font-mono">
                      JSON
                    </span>
                  </button>
                  {openSections.raw && (
                    <div
                      ref={rawRef}
                      className="p-4 text-xs text-gray-300 font-mono whitespace-pre-wrap max-h-48 overflow-auto border-t border-white/5"
                    >
                      {errorDetails.raw}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
  bg-white/5 hover:bg-white/10 text-gray-200
  border border-white/10 transition"
            >
              <ArrowLeft size={16} /> {t("back")}
            </button>

            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
  bg-blue-600 hover:bg-blue-500 text-white
  transition shadow"
            >
              <Home size={16} /> {t("errorPage.home")}
            </button>

            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
  bg-white/5 hover:bg-white/10 text-gray-200
  border border-white/10 transition"
            >
              <RefreshCcw size={16} /> {t("errorPage.reload")}
            </button>

            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
  bg-white/5 hover:bg-white/10 text-gray-200
  border border-white/10 transition"
            >
              {copied ? (
                <Check size={16} className="text-emerald-400" />
              ) : (
                <Copy size={16} />
              )}
              {copied ? t("errorPage.copied") : t("errorPage.copy")}
            </button>

            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
  bg-white/5 hover:bg-white/10 text-gray-200
  border border-white/10 transition"
            >
              <Download size={16} /> {t("actions.download")}
            </button>
          </div>

          {/* SUPPORT CTA */}
          <div>
            <button
              onClick={handleMail}
              className="
                                px-6 py-3 rounded-xl text-sm font-medium
                                bg-emerald-600 hover:bg-emerald-500
                                shadow-lg shadow-emerald-900/30
                                flex items-center gap-2 mx-auto
                                transition text-white
                            "
            >
              <Mail size={16} /> {t("errorPage.contactSupport")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
